import React, { useState } from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, NoteContactCard, Text } from '@components'
import { Contact } from '@hooks'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import { useNotePerson } from '../hooks'
import { NotePersonPicker } from './NotePersonPicker'

interface NoteAboutProps {
  contactId: string | null
  // The note being written, left out of « N autres notes ».
  currentNoteId?: string
  canChange: boolean
  // Where the list of profiles opens: under the field (panel on the right of
  // the computer editor) or over it (bottom of the side panel).
  placement?: 'below' | 'above'
  onSelect: (contact: Contact) => void
  // A note about nobody again.
  onRemove: () => void
  onOpenProfile: () => void
  onOpenNotes: () => void
}

interface NoteAboutCardProps {
  contactId: string
  currentNoteId?: string
  onChange?: () => void
  onOpenProfile: () => void
  onOpenNotes: () => void
}

// The card of the chosen person, with when you last talked and how many other
// notes you have on them.
const NoteAboutCard = ({
  contactId,
  currentNoteId,
  onChange,
  onOpenProfile,
  onOpenNotes,
}: NoteAboutCardProps) => {
  const person = useNotePerson(contactId, currentNoteId)

  return (
    <NoteContactCard
      name={person.name}
      avatarUrl={person.avatarUrl}
      lastExchange={person.lastExchange}
      otherNotesCount={person.otherNotesCount}
      onOpenProfile={onOpenProfile}
      onOpenNotes={onOpenNotes}
      onChange={onChange}
    />
  )
}

// « À propos de » of the note editor: the chosen person's card, or a field to
// choose one. The list of profiles opens over the rest of the panel.
export const NoteAbout = ({
  contactId,
  currentNoteId,
  canChange,
  placement = 'below',
  onSelect,
  onRemove,
  onOpenProfile,
  onOpenNotes,
}: NoteAboutProps) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  const choose = (contact: Contact) => {
    setIsPickerOpen(false)
    onSelect(contact)
  }
  const remove = () => {
    setIsPickerOpen(false)
    onRemove()
  }

  return (
    <View style={styles.container}>
      <Text type="bodySmallBold" color={theme.colors.greyBlue}>
        {t('notesForm.about')}
      </Text>
      {contactId && !isPickerOpen ? (
        <NoteAboutCard
          contactId={contactId}
          currentNoteId={currentNoteId}
          onChange={canChange ? () => setIsPickerOpen(true) : undefined}
          onOpenProfile={onOpenProfile}
          onOpenNotes={onOpenNotes}
        />
      ) : (
        <View style={styles.fieldWrapper}>
          <TouchableOpacity
            testID="note-choose-person"
            style={[styles.field, isPickerOpen && styles.fieldOpen]}
            onPress={() => setIsPickerOpen(!isPickerOpen)}
            disabled={!canChange}
          >
            <Icon name="userPlus" size={20} color={theme.colors.primary} />
            <Text type="body" color={theme.colors.primary} style={styles.fieldText}>
              {t('notesForm.choosePerson')}
            </Text>
            <Icon name="arrowDown" size={18} color={theme.colors.primary} />
          </TouchableOpacity>
          {isPickerOpen && (
            <View style={placement === 'above' ? styles.dropdownAbove : styles.dropdown}>
              <NotePersonPicker selectedContactId={contactId} onSelect={choose} onRemove={remove} />
            </View>
          )}
        </View>
      )}
    </View>
  )
}

// The field is 52 px high; the list leaves a 6 px gap.
const DROPDOWN_OFFSET = 58

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    // Above the next sections, which the open list covers.
    container: {
      zIndex: 10,
    },
    fieldWrapper: {
      marginTop: theme.spacing.sm,
    },
    field: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      height: 52,
      paddingHorizontal: 14,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: theme.colors.primary,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
    },
    fieldOpen: {
      borderStyle: 'solid',
    },
    fieldText: {
      flex: 1,
    },
    dropdown: {
      position: 'absolute',
      top: DROPDOWN_OFFSET,
      left: 0,
      right: 0,
    },
    dropdownAbove: {
      position: 'absolute',
      bottom: DROPDOWN_OFFSET,
      left: 0,
      right: 0,
    },
  })
